// Call Notes — the pilot template for the MCP document-draft surface.
//
// The field list is the single source of truth: the web renderer
// (`apps/web/src/lib/pdf-templates/call-notes.tsx`) imports it from here, and
// the MCP `describe_document_template` tool hands the same array to the
// filling model. A blank cannot exist in one place and not the other.

import type { DocumentFieldSpec, DocumentTemplateSpec } from './types';

/** Sentiment bands the template renders a coloured band for. */
export const CALL_NOTE_SENTIMENTS = ['positive', 'neutral', 'negative', 'escalation'] as const;

export const CALL_NOTES_FIELDS: ReadonlyArray<DocumentFieldSpec> = [
    { key: 'clientName', label: 'Client name', type: 'text', required: true, maxLength: 200 },
    { key: 'callDate', label: 'Call date (ISO)', type: 'date', required: true },
    {
        key: 'duration',
        label: 'Duration',
        type: 'text',
        required: true,
        hint: 'e.g. 27 min',
        maxLength: 40,
    },
    {
        key: 'imName',
        label: 'Investment manager',
        type: 'text',
        required: true,
        maxLength: 120,
    },
    { key: 'participants', label: 'Participants', type: 'list', required: true, maxLength: 20 },
    {
        key: 'summary',
        label: 'Call summary',
        type: 'paragraph',
        required: true,
        maxLength: 600,
    },
    { key: 'actionItems', label: 'Action items', type: 'list', required: false, maxLength: 20 },
    {
        key: 'sentimentNote',
        label: 'Sentiment note',
        type: 'paragraph',
        required: true,
        maxLength: 300,
    },
    {
        key: 'sentiment',
        label: 'Sentiment band',
        type: 'text',
        required: true,
        // Enforced, not hinted: the renderer keys its coloured band off this
        // value, so anything outside the set renders as nothing while the
        // reviewer's screen still shows whatever text was submitted.
        enum: CALL_NOTE_SENTIMENTS,
    },
] as const;

export const CALL_NOTES_TEMPLATE: DocumentTemplateSpec = {
    slug: 'call-notes',
    displayName: 'Call Notes',
    versionTag: 'call-notes@2026-q2',
    summary:
        'A file note summarising one client call: what was discussed, agreed action items, and the tone of the conversation. Filed against the client record after the named investment manager approves it.',
    subjectKind: 'call',
    fields: CALL_NOTES_FIELDS,
};
